/*
 * @Author: Ryan
 * @Date: 2026-02-02
 * @Description: Testimonials section - What Users Say About AI Image Translator
 */
import React from 'react';

interface Testimonial {
  title: string;
  content: string;
  role: string;
  name: string;
}

const testimonials: Testimonial[] = [
  {
    title: 'A Game Changer for Global E-Commerce',
    content:
      "FalcoCut helped us localize product images faster than ever. Translating packaging and banners used to take days—now it's done in minutes, and the layout stays perfect.",
    role: 'Online Store Manager',
    name: 'Jenson Robles',
  },
  {
    title: 'Perfect for Travel Content',
    content:
      "I use FalcoCut to translate menus, signs, and travel guides for my blog. It's fast, accurate, and keeps the original design intact. My readers love the clarity across languages.",
    role: 'Travel Blogger',
    name: 'Marco Ruiz',
  },
  {
    title: 'Makes Study Materials Accessible',
    content:
      "FalcoCut lets me translate diagrams and scanned notes for my students without losing formatting. It's easy to use and saves hours of manual work.",
    role: 'University Lecturer',
    name: 'Tracy Cartwright',
  },
  {
    title: 'Reliable for Team Collaboration',
    content:
      "We use FalcoCut to translate internal documents and presentations for our global team. It's secure, fast, and accurate—no more back-and-forth with external translators.",
    role: 'Operations Lead',
    name: 'Daniel Cho',
  },
  {
    title: 'Boosted My Social Reach',
    content:
      'Translating memes and image posts used to be a hassle. With FalcoCut, I can connect with fans in multiple languages while keeping my style intact.',
    role: 'Digital Creator',
    name: 'Nina Park',
  },
  {
    title: 'Effortless Menu Translation',
    content:
      'FalcoCut helps us translate our restaurant menus for tourists without needing a designer. The results look polished and professional, and updates are easy to manage.',
    role: 'Restaurant Owner',
    name: 'Jean-Luc Moreau',
  },
];

const StarRating: React.FC = () => (
  <div className="flex gap-1 mb-3 pad:mb-4">
    {[1, 2, 3, 4, 5].map((star) => (
      <svg
        key={star}
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M10 1L12.39 6.26L18 7.27L14 11.14L14.76 17L10 14.27L5.24 17L6 11.14L2 7.27L7.61 6.26L10 1Z"
          fill="#FBBF24"
          stroke="#FBBF24"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ))}
  </div>
);

const TestimonialCard: React.FC<Testimonial> = ({ title, content, role, name }) => {
  return (
    <div className="bg-white rounded-2xl p-6 pad:p-8 h-full flex flex-col">
      <StarRating />
      <h3 className="text-lg pad:text-xl font-bold text-gray-900 mb-3 pad:mb-4">
        {title}
      </h3>
      <p className="text-sm pad:text-base text-gray-600 leading-relaxed flex-grow mb-4 pad:mb-6">
        {content}
      </p>
      <div>
        <p className="font-semibold text-gray-900 text-sm pad:text-base">{role}</p>
        <p className="text-gray-500 text-sm">{name}</p>
      </div>
    </div>
  );
};

const TestimonialsSection: React.FC = () => {
  return (
    <section className="w-full bg-[#F5F7FB] py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-10 pad:mb-14 sm:mb-16 leading-tight">
          What Our Customers Say
        </h2>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 pad:grid-cols-2 sm:grid-cols-3 gap-4 pad:gap-6">
          {testimonials.map((testimonial, index) => (
            <TestimonialCard
              key={index}
              title={testimonial.title}
              content={testimonial.content}
              role={testimonial.role}
              name={testimonial.name}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
